import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  type SendLocationNotificationPayload,
  type SendNotificationPayload,
  notificationsProvider,
} from "@/providers/notifications.provider";
import { IconSend } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

const DEFAULT_USER_FORM: SendNotificationPayload = {
  action_type: "system_alert",
  body: "",
  redirect_path: "",
  actor_user_id: null,
  icon_obj_path: null,
};

const DEFAULT_LOCATION_FORM: SendLocationNotificationPayload = {
  lat: 0,
  lng: 0,
  radius_meters: 5000,
  action_type: "system_alert",
  body: "",
  redirect_path: "",
  actor_user_id: null,
  icon_obj_path: null,
};

export function NotificationsPage() {
  return (
    <div className="flex flex-col h-full">
      <SiteHeader title="Notifications" />

      <Tabs defaultValue="by-user" className="flex flex-col flex-1 min-h-0">
        <div className="px-4 lg:px-6 pt-2">
          <TabsList>
            <TabsTrigger value="by-user">By User</TabsTrigger>
            <TabsTrigger value="by-location">By Location</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="by-user" className="flex-1 p-4 lg:p-6 mt-0">
          <ByUserForm />
        </TabsContent>

        <TabsContent value="by-location" className="flex-1 p-4 lg:p-6 mt-0">
          <ByLocationForm />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ByUserForm() {
  const [recipientUserId, setRecipientUserId] = useState("");
  const [form, setForm] = useState<SendNotificationPayload>(DEFAULT_USER_FORM);

  const send = useMutation({
    mutationFn: () => notificationsProvider.sendToUser(recipientUserId, form),
    onSuccess: () => {
      toast.success("Notification sent");
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="flex flex-col gap-4 max-w-md">
      <div>
        <h2 className="text-sm font-semibold">Send notification</h2>
        <p className="text-sm text-muted-foreground">
          Manually send a notification to any user by ID.
        </p>
      </div>

      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          send.mutate();
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="recipient_user_id">Recipient user ID</Label>
          <Input
            id="recipient_user_id"
            value={recipientUserId}
            onChange={(e) => setRecipientUserId(e.target.value)}
            placeholder="User UUID"
            required
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="action_type">Action type</Label>
          <Input
            id="action_type"
            value={form.action_type}
            onChange={(e) =>
              setForm((f) => ({ ...f, action_type: e.target.value }))
            }
            placeholder="system_alert"
            required
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="body">Body</Label>
          <Textarea
            id="body"
            value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            placeholder="Your account has been reviewed."
            rows={3}
            required
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="icon_obj_path">
            Icon object path{" "}
            <span className="text-muted-foreground font-normal">
              (optional)
            </span>
          </Label>
          <Input
            id="icon_obj_path"
            value={form.icon_obj_path ?? ""}
            onChange={(e) =>
              setForm((f) => ({ ...f, icon_obj_path: e.target.value || null }))
            }
            placeholder="avatars/user_123.png"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="redirect_path">Redirect path</Label>
          <Input
            id="redirect_path"
            value={form.redirect_path}
            onChange={(e) =>
              setForm((f) => ({ ...f, redirect_path: e.target.value }))
            }
            placeholder="/profile"
            required
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="actor_user_id">
            Actor user ID{" "}
            <span className="text-muted-foreground font-normal">
              (optional)
            </span>
          </Label>
          <Input
            id="actor_user_id"
            value={form.actor_user_id ?? ""}
            onChange={(e) =>
              setForm((f) => ({ ...f, actor_user_id: e.target.value || null }))
            }
            placeholder="Leave blank for system"
          />
        </div>

        <Button type="submit" disabled={send.isPending} className="gap-2 self-start">
          <IconSend className="size-4" />
          {send.isPending ? "Sending…" : "Send notification"}
        </Button>
      </form>
    </div>
  );
}

function ByLocationForm() {
  const [form, setForm] = useState<SendLocationNotificationPayload>(
    DEFAULT_LOCATION_FORM,
  );
  const [coordsPaste, setCoordsPaste] = useState("");
  const [coordsError, setCoordsError] = useState<string | null>(null);

  function applyCoordsPaste(value: string) {
    setCoordsPaste(value);
    if (!value.trim()) {
      setCoordsError(null);
      return;
    }
    try {
      const parsed = JSON.parse(value);
      const lat = Number(parsed.lat);
      const lng = Number(parsed.lng);
      if (Number.isNaN(lat) || Number.isNaN(lng)) {
        throw new Error("Missing lat/lng");
      }
      setForm((f) => ({ ...f, lat, lng }));
      setCoordsError(null);
    } catch {
      setCoordsError("Expecting JSON like { \"lat\": 0, \"lng\": 0 }");
    }
  }

  const send = useMutation({
    mutationFn: () => notificationsProvider.sendToLocation(form),
    onSuccess: () => {
      toast.success("Notification sent");
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="flex flex-col gap-4 max-w-md">
      <div>
        <h2 className="text-sm font-semibold">Send notification</h2>
        <p className="text-sm text-muted-foreground">
          Broadcast a notification to every user within the given radius of
          a location — both users currently online in the area and users
          whose last known login location was there (up to 30 days stale).
          The notification is persisted and delivered in real time to
          connected users; offline users receive it once they reconnect.
        </p>
      </div>

      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          send.mutate();
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="coords_paste">
            Paste coordinates{" "}
            <span className="text-muted-foreground font-normal">
              (optional — fills latitude/longitude below)
            </span>
          </Label>
          <Textarea
            id="coords_paste"
            value={coordsPaste}
            onChange={(e) => applyCoordsPaste(e.target.value)}
            placeholder='{ "lat": -23.10312601026358, "lng": -47.234289208909246 }'
            rows={2}
            className="font-mono text-sm"
          />
          {coordsError && (
            <p className="text-sm text-destructive">{coordsError}</p>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="lat">Latitude</Label>
            <Input
              id="lat"
              type="number"
              step="any"
              min={-90}
              max={90}
              value={form.lat}
              onChange={(e) =>
                setForm((f) => ({ ...f, lat: Number(e.target.value) }))
              }
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="lng">Longitude</Label>
            <Input
              id="lng"
              type="number"
              step="any"
              min={-180}
              max={180}
              value={form.lng}
              onChange={(e) =>
                setForm((f) => ({ ...f, lng: Number(e.target.value) }))
              }
              required
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="radius_meters">
            Radius{" "}
            <span className="text-muted-foreground font-normal">
              (meters, e.g. 5000 for a 5 km radius)
            </span>
          </Label>
          <Input
            id="radius_meters"
            type="number"
            min={1}
            step="any"
            value={form.radius_meters}
            onChange={(e) =>
              setForm((f) => ({ ...f, radius_meters: Number(e.target.value) }))
            }
            required
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="loc_action_type">Action type</Label>
          <Input
            id="loc_action_type"
            value={form.action_type}
            onChange={(e) =>
              setForm((f) => ({ ...f, action_type: e.target.value }))
            }
            placeholder="system_alert"
            required
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="loc_body">Body</Label>
          <Textarea
            id="loc_body"
            value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            placeholder="Heavy rain expected in your area."
            rows={3}
            required
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="loc_redirect_path">Redirect path</Label>
          <Input
            id="loc_redirect_path"
            value={form.redirect_path}
            onChange={(e) =>
              setForm((f) => ({ ...f, redirect_path: e.target.value }))
            }
            placeholder="/home"
            required
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="loc_icon_obj_path">
            Icon object path{" "}
            <span className="text-muted-foreground font-normal">
              (optional)
            </span>
          </Label>
          <Input
            id="loc_icon_obj_path"
            value={form.icon_obj_path ?? ""}
            onChange={(e) =>
              setForm((f) => ({ ...f, icon_obj_path: e.target.value || null }))
            }
            placeholder="icons/weather-alert.png"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="loc_actor_user_id">
            Actor user ID{" "}
            <span className="text-muted-foreground font-normal">
              (optional)
            </span>
          </Label>
          <Input
            id="loc_actor_user_id"
            value={form.actor_user_id ?? ""}
            onChange={(e) =>
              setForm((f) => ({ ...f, actor_user_id: e.target.value || null }))
            }
            placeholder="Leave blank for system"
          />
        </div>

        <Button type="submit" disabled={send.isPending} className="gap-2 self-start">
          <IconSend className="size-4" />
          {send.isPending ? "Sending…" : "Send notification"}
        </Button>
      </form>
    </div>
  );
}
